import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {ButtonGroup} from '../../../components/form'
import {JSONField} from '../../../components/library/json-field'
import {Avatar} from '../../../components/library/avatar/avatar'
import {Button} from '../../../components/library/buttons/index'
import {Copyable, CopyInput} from '../../../components/library/copy'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {TextInput} from '../../../components/library/form/text'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResults,
  NoResultsPermissions
} from '../../../components/library/list/index'
import {Modal} from '../../../components/library/modal/modal'
import styled from 'styled-components'
import {Tab, TabNav} from '../../../components/library/tab/tab'
import {Tooltip} from '../../../components/library/tooltip/tooltip'
import {
  MagicLinkComposer,
  MagicLinkModalTypes
} from '../../../components/magic-link-composer'
import {FormModal} from '../../../components/modals'
import {OverflowFooter} from '../../../components/overflow'
import {
  CustomEndUserDetailFragment,
  UpdateEndUserMutationVariables,
  useCreateTestEndUserMutation,
  useRemoveEndUserMutation,
  useTestUserListLazyQuery,
  useUpdateEndUserMutation
} from '../../../data/workbench-api/generated/end-user'
import {
  PermissionAction,
  PermissionResource,
  SyncedEndUsersOrderBy
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useEnvPreferences} from '../../../hooks/use-env-preferences'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {toTitleCase} from '../../../lib/helpers'
import {
  DeleteLink,
  IconHeading,
  Mono,
  Section,
  SectionContent,
  SectionInfo
} from '../components'

const Details = styled.div`
  display: flex;
`

const MagicLink = styled.div`
  margin-left: 10px;
`

const Primary = styled.div`
  color: ${p => p.theme.grey100};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -5px;
`

const Text = styled.div`
  display: flex;
  margin-left: 10px;
`

const Secondary = styled.div`
  color: ${p => p.theme.grey50};
  margin-left: 10px;
`

const TabPositioner = styled.div`
  margin: -10px -30px 30px;
`

const ModalMessage = styled.div`
  color: ${({theme}) => theme.grey50};
  line-height: ${({theme}) => theme.lineHeight};
  margin-bottom: 20px;
`

const endUserIsValid = (obj: {id?: unknown; nickname?: unknown}) =>
  !!(obj.id && obj.nickname)

const Overflow = ({
  endUser,
  closeDropdown,
  refetch,
  //edit,
  rename,
  revealAuthSecret,
  environment
}: {
  endUser: CustomEndUserDetailFragment
  closeDropdown: () => void
  refetch: () => void
  //edit: () => void
  rename: () => void
  revealAuthSecret?: () => void
  environment: EnvironmentNode
}) => {
  const analytics = useAnalytics()
  const [deleteUser] = useRemoveEndUserMutation({
    onCompleted: () => {
      analytics.track({
        event: 'Edited configuration',
        properties: {
          org_id: environment.organisationId,
          environment_id: environment.id,
          category: 'configuration>testing>test-accounts'
        }
      })
    }
  })

  return (
    <>
      {!endUser.organisationBusinessUserAccount || revealAuthSecret ? (
        <>
          <DropdownBody>
            {revealAuthSecret ? (
              <>
                <DropdownItem>
                  <a
                    onClick={() => {
                      revealAuthSecret()
                      closeDropdown()
                    }}
                  >
                    Reveal auth secret
                  </a>
                </DropdownItem>
              </>
            ) : null}
            {environment.hasPermission(
              PermissionResource.BusinessUser,
              PermissionAction.Edit
            ) && !endUser.organisationBusinessUserAccount ? (
              <>
                <DropdownItem>
                  <a
                    onClick={() => {
                      rename()
                      closeDropdown()
                    }}
                  >
                    Rename
                  </a>
                </DropdownItem>
                <DropdownItem>
                  <DeleteLink
                    onClick={() => {
                      if (
                        confirm(
                          `Are you sure you want to delete ${endUser.nickname}?`
                        )
                      ) {
                        deleteUser({
                          variables: {
                            id: endUser.id,
                            environmentId: endUser.environmentId
                          }
                        }).then(refetch)
                      }
                    }}
                  >
                    Delete
                  </DeleteLink>
                </DropdownItem>
              </>
            ) : null}
          </DropdownBody>

          <DropdownFooter>
            <OverflowFooter created={endUser.created} />
          </DropdownFooter>
        </>
      ) : (
        <DropdownBody>
          <OverflowFooter created={endUser.created} />
        </DropdownBody>
      )}
    </>
  )
}

type CurrentTab = 'team' | 'created'

type CurrentModal =
  | {
      type: 'new'
    }
  | {
      type: 'edit'
      endUser: CustomEndUserDetailFragment
    }
  | {
      type: 'rename'
      endUser: CustomEndUserDetailFragment
    }
  | {
      type: 'authSecret'
      endUser: CustomEndUserDetailFragment
    }
  | {
      type: 'magicLink'
      context: MagicLinkModalTypes
      endUser?: CustomEndUserDetailFragment
    }

export const TestAccounts = ({environment}: {environment: EnvironmentNode}) => {
  const canEdit = environment.hasPermission(
    PermissionResource.BusinessUser,
    PermissionAction.Edit
  )
  const canView = environment.hasPermission(
    PermissionResource.BusinessUser,
    PermissionAction.View
  )

  const orderBy = [SyncedEndUsersOrderBy.NicknameAsc]
  const [query, {data, refetch = () => null, loading}] =
    useTestUserListLazyQuery({
      variables: {
        environmentId: environment.id
      }
    })
  const {loading: loadingPreferences, preferences} = useEnvPreferences(
    environment.id
  )
  const envRequiresMagicLinkAuth =
    preferences?.magicLinkConfiguration?.requiresAuthentication

  useEffect(() => {
    if (canView) {
      query()
    }
  }, [canView, query])

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed test accounts configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>testing>test-accounts'
      }
    })
  }, [analytics, environment])

  const endUsers = data?.endUsers?.nodes

  const [currentTab, setCurrentTab] = useState<CurrentTab>('team')
  const [currentModal, setCurrentModal] = useState<CurrentModal | null>(null)

  const teamUserColumns: ListColumns<
    CustomEndUserDetailFragment,
    SyncedEndUsersOrderBy
  > = [
    {
      id: 'user',
      accessor: r =>
        r.organisationBusinessUserAccount ? (
          r.organisationBusinessUserAccount?.name ? (
            <Primary>
              <Avatar name={r.organisationBusinessUserAccount.email} />
              <Text>
                {toTitleCase(r.organisationBusinessUserAccount.name)}
                <Secondary>{r.organisationBusinessUserAccount.email}</Secondary>
              </Text>
            </Primary>
          ) : (
            <Primary>
              <Avatar name={r.organisationBusinessUserAccount.email} />
              <Text>{r.organisationBusinessUserAccount.email}</Text>
            </Primary>
          )
        ) : (
          <Primary>
            <Avatar name={r.nickname} />
            <Text>{toTitleCase(r.nickname)}</Text>
          </Primary>
        )
    },
    {
      id: 'details',
      horizontalAlign: 'right',
      accessor: r => (
        <Details>
          <Tooltip
            content={<Mono>{r.id}</Mono>}
            isLight={false}
            position="left"
          >
            <Copyable value={r.id} copyText="Copy ID" />
          </Tooltip>
          {r.authSecret && !loadingPreferences && !envRequiresMagicLinkAuth && (
            <MagicLink>
              <Button
                appearance="secondary"
                onClick={() =>
                  setCurrentModal({
                    type: 'magicLink',
                    endUser: r,
                    context: 'user'
                  })
                }
                leftAlign={true}
              >
                Get magic auth link
              </Button>
            </MagicLink>
          )}
        </Details>
      )
    }
  ]

  const externalAccountColumns: ListColumns<
    CustomEndUserDetailFragment,
    SyncedEndUsersOrderBy
  > = [
    {
      id: 'nickname',
      header: 'Nickname',
      accessor: r => (
        <Primary>
          <Avatar name={r.nickname} />
          <Text>{toTitleCase(r.nickname)}</Text>
        </Primary>
      )
    },
    {
      id: 'details',
      horizontalAlign: 'right',
      accessor: r => (
        <Details>
          <Tooltip
            content={<Mono>{r.id}</Mono>}
            isLight={false}
            position="left"
          >
            <Copyable value={r.id} copyText="Copy ID" />
          </Tooltip>
          <MagicLink>
            <Button
              appearance="secondary"
              onClick={() =>
                setCurrentModal({
                  type: 'magicLink',
                  endUser: r,
                  context: 'user'
                })
              }
              leftAlign={true}
            >
              Get magic auth link
            </Button>
          </MagicLink>
        </Details>
      )
    }
  ]

  return (
    <Section>
      <Helmet>
        <title>Test accounts</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="profile" /> Test accounts
        </IconHeading>

        <nav>
          <Button
            appearance="secondary"
            disabled={
              !(
                environment.hasPermission(
                  PermissionResource.StreamContainer,
                  PermissionAction.View
                ) && canView
              )
            }
            onClick={() => setCurrentModal({type: 'magicLink', context: 'new'})}
            style={{marginRight: '10px'}}
          >
            New magic auth link
          </Button>
          <Button
            disabled={!canEdit}
            onClick={() => setCurrentModal({type: 'new'})}
          >
            New test account
          </Button>
        </nav>
      </header>

      <SectionInfo>
        <p>Create and manage test accounts for your SDK integrations</p>
      </SectionInfo>
      <TabPositioner>
        <TabNav>
          <Tab
            current={currentTab === 'team'}
            onClick={() => {
              setCurrentTab('team')
            }}
          >
            Team
          </Tab>
          <Tab
            current={currentTab === 'created'}
            onClick={() => {
              setCurrentTab('created')
            }}
          >
            External
          </Tab>
        </TabNav>
      </TabPositioner>
      <SectionContent>
        <List<CustomEndUserDetailFragment, SyncedEndUsersOrderBy>
          displayHeader={false}
          columns={
            currentTab === 'team' ? teamUserColumns : externalAccountColumns
          }
          rows={
            currentTab === 'team'
              ? endUsers?.filter(r => !!r.organisationBusinessUserAccount)
              : endUsers?.filter(r => !r.organisationBusinessUserAccount)
          }
          renderOverflow={(endUser, closeDropdown) => (
            <Overflow
              endUser={endUser}
              environment={environment}
              rename={() =>
                setCurrentModal({
                  type: 'rename',
                  endUser
                })
              }
              revealAuthSecret={
                endUser.authSecret && !envRequiresMagicLinkAuth
                  ? () =>
                      setCurrentModal({
                        type: 'authSecret',
                        endUser
                      })
                  : undefined
              }
              refetch={refetch}
              closeDropdown={closeDropdown}
            />
          )}
          loading={loading}
          orderBy={orderBy}
          bleed={'#f2f2f2'}
          renderEmpty={
            canView
              ? () => (
                  <NoResults
                    icon="profile"
                    heading="No accounts"
                    message="No one to show here yet"
                  />
                )
              : () => <NoResultsPermissions />
          }
        />
      </SectionContent>

      {currentModal && currentModal.type === 'rename' ? (
        <RenameUserModal
          endUser={currentModal.endUser}
          handleClose={() => setCurrentModal(null)}
          onCompleted={() => {
            analytics.track({
              event: 'Edited configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>testing>test-accounts'
              }
            })
            setCurrentModal(null)
            refetch()
          }}
        />
      ) : null}

      {currentModal && currentModal.type === 'edit' ? (
        <EditPreferenceModal
          endUser={currentModal.endUser}
          handleClose={() => setCurrentModal(null)}
          onCompleted={() => {
            analytics.track({
              event: 'Edited configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>testing>test-accounts'
              }
            })
            setCurrentModal(null)
          }}
        />
      ) : null}

      {currentModal && currentModal.type === 'new' ? (
        <NewEndUserModal
          environmentId={environment.id}
          handleClose={() => setCurrentModal(null)}
          onCompleted={() => {
            analytics.track({
              event: 'Added configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category: 'configuration>testing>test-accounts'
              }
            })
            setCurrentModal(null)
            setCurrentTab('created')
            refetch()
          }}
        />
      ) : null}

      {currentModal && currentModal.type === 'authSecret' ? (
        <Modal
          title={`Auth secret for ${
            currentModal.endUser.organisationBusinessUserAccount?.name ??
            currentModal.endUser.nickname
          }`}
          handleClose={() => setCurrentModal(null)}
        >
          <CopyInput value={currentModal.endUser.authSecret ?? ''} />
          <ButtonGroup>
            <Button size="large" onClick={() => setCurrentModal(null)}>
              Done
            </Button>
          </ButtonGroup>
        </Modal>
      ) : null}

      {currentModal && currentModal.type === 'magicLink' && (
        <Modal
          handleClose={() => setCurrentModal(null)}
          style={{maxWidth: '400px', padding: '0px'}}
          isFullScreen={false}
        >
          <MagicLinkComposer
            environmentId={environment.id}
            organisationId={environment.organisationId}
            handleClose={() => setCurrentModal(null)}
            defaultUserId={currentModal?.endUser?.id}
            context={currentModal.context}
            userName={currentModal.endUser?.nickname}
          />
        </Modal>
      )}
    </Section>
  )
}

interface RenameEndUserModalProps {
  endUser: CustomEndUserDetailFragment
  handleClose: () => void
  onCompleted: () => void
}
const RenameUserModal = ({
  endUser,
  handleClose,
  onCompleted
}: RenameEndUserModalProps) => {
  const [localObj, updateLocalObj, dirty] =
    useStateChanged<UpdateEndUserMutationVariables>({
      environmentId: endUser.environmentId,
      id: endUser.id,
      nickname: endUser.nickname
    })
  const [doMutation, {loading, error}] = useUpdateEndUserMutation({
    onCompleted
  })

  return (
    <FormModal
      title={'Rename ' + endUser.nickname}
      handleClose={handleClose}
      error={error}
      onSubmit={() =>
        doMutation({
          variables: localObj
        })
      }
      submitText="Rename"
      isValid={endUserIsValid(localObj) && !loading && dirty}
    >
      <TextInput
        style={{maxWidth: '100%', marginBottom: '20px'}}
        label="Give them a name..."
        upLabel="Name"
        value={localObj.nickname || ''}
        onChange={nickname => updateLocalObj(c => ({...c, nickname}))}
      />
    </FormModal>
  )
}

interface EditPreferenceUserModalProps {
  endUser: CustomEndUserDetailFragment
  handleClose: () => void
  onCompleted: () => void
}
const EditPreferenceModal = ({
  endUser,
  handleClose,
  onCompleted
}: EditPreferenceUserModalProps) => {
  const [JSON, setJSON] = useState<object>({})
  return (
    <FormModal
      title={'Edit preferences for ' + endUser.nickname}
      handleClose={handleClose}
      onSubmit={onCompleted}
      submitText="Save"
    >
      <ModalMessage>
        Set preferences for this test account, such as the default timezone and
        notification delivery window.
      </ModalMessage>
      <JSONField value={JSON} onChange={setJSON} raw={true} legacyMode={true} />
    </FormModal>
  )
}

interface NewEndUserModalProps {
  environmentId: string
  handleClose: () => void
  onCompleted: () => void
}
const NewEndUserModal = ({
  environmentId,
  handleClose,
  onCompleted
}: NewEndUserModalProps) => {
  const [endUser, updateEndUser] = useState<{
    environmentId: string
    nickname: string
    id: string
  }>({
    environmentId,
    nickname: '',
    id: ''
  })
  const [doMutation, {loading, error}] = useCreateTestEndUserMutation({
    onCompleted
  })

  return (
    <FormModal
      title="Add new test account"
      handleClose={handleClose}
      error={error?.graphQLErrors[0].message ?? error?.message}
      onSubmit={() =>
        doMutation({
          variables: endUser
        })
      }
      submitText="Add"
      isValid={endUserIsValid(endUser) && !loading}
    >
      <TextInput
        style={{maxWidth: '100%', marginBottom: '20px'}}
        label="Give them a name..."
        upLabel="Name"
        value={endUser.nickname}
        onChange={nickname => updateEndUser(c => ({...c, nickname}))}
      />
      <TextInput
        label="Give them a unique user ID..."
        upLabel="User ID"
        value={endUser.id}
        onChange={id => updateEndUser(c => ({...c, id}))}
      />
    </FormModal>
  )
}
